import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Breadcrumbs,
  Link,
  // Badge,
  Box,
  Button,
  Avatar,
  // Menu,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useLocation, Link as RouterLink, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import langicon from "../assets/img/systemtranslate.svg";
// import { getAuth, signOut } from "firebase/auth";
import { api } from "common";

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  boxShadow: "none",
  borderBottom: "1px solid #E5E7EB",
  "& .MuiToolbar-root": {
    minHeight: "64px",
    padding: "0 24px",
  },
}));

const StyledBreadcrumbs = styled(Breadcrumbs)({
  "& .MuiBreadcrumbs-separator": {
    color: "#8B8D97",
  },
});

const StyledLink = styled(Link)({
  color: "#8B8D97",
  fontSize: "14px",
  textDecoration: "none",
  display: "flex",
  alignItems: "center",
  gap: "4px",
  "&:hover": {
    textDecoration: "none",
  },
});

const LanguageButton = styled(Button)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  color: "#1C1D22",
  textTransform: "none",
  "& img": {
    width: "20px",
    height: "20px",
    filter: "brightness(0)",
  },
});

const LanguageMenu = styled(Box)({
  position: "absolute",
  top: "100%",
  right: 0,
  backgroundColor: "#FFFFFF",
  borderRadius: "8px",
  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
  padding: "8px 0",
  zIndex: 1000,
  "& .MuiMenuItem-root": {
    color: "#1C1D22",
    fontSize: "14px",
    padding: "8px 16px",
    "&:hover": {
      backgroundColor: "#F3F4F6",
    },
  },
});

export default function CustomHeader({
  drawerWidth,
  isRTL,
  handleDrawerToggle,
  settings,
  auth,
}) {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileMenu, setProfileMenu] = React.useState(null);
  const dispatch = useDispatch();
  const languagedata = useSelector((state) => state.languagedata);
  const [langSelection, setLangSelection] = useState();
  const [multiLanguage, setMultiLanguage] = useState();
  const [toggleLang, setToggleLang] = useState(false);

  const { signOff } = api;

  const pathnames = location.pathname.split("/").filter((x) => x);

  useEffect(() => {
    if (languagedata.langlist) {
      for (const key of Object.keys(languagedata.langlist)) {
        if (languagedata.langlist[key].langLocale === i18n.language) {
          setLangSelection(key);
        }
      }
      setMultiLanguage(languagedata.langlist);
    }
  }, [languagedata.langlist, i18n.language]);

  const handleLanguageSelect = (index) => {
    i18n.addResourceBundle(
      multiLanguage[index].langLocale,
      "translations",
      multiLanguage[index].keyValuePairs
    );
    i18n.changeLanguage(multiLanguage[index].langLocale);
    setLangSelection(index);
    moment.locale(multiLanguage[index].dateLocale);
    setToggleLang(false);
  };

  const getPathLabel = (path) => {
    return t(path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, " "));
  };

  // const handleLogout = async () => {
  //   const auth = getAuth();
  //   try {
  //     await signOut(auth);
  //     dispatch(signOff());
  //     navigate("/login");
  //     setProfileMenu(null);
  //   } catch (error) {
  //     console.error("Logout error:", error);
  //   }
  // };

  return (
    <StyledAppBar
      position="fixed"
      sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: isRTL === "rtl" ? 0 : `${drawerWidth}px` },
        mr: { sm: isRTL === "rtl" ? `${drawerWidth}px` : 0 },
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        {/* Left section with menu icon and breadcrumbs */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon style={{ color: "#1C1D22" }} />
          </IconButton>

          <StyledBreadcrumbs aria-label="breadcrumb">
            <StyledLink component={RouterLink} to="/" sx={{ color: "#8B8D97" }}>
              <HomeIcon sx={{ width: 20, height: 20 }} />
            </StyledLink>
            {pathnames.map((path, index) => {
              const routeTo = `/${pathnames.slice(0, index + 1).join("/")}`;
              const isLast = index === pathnames.length - 1;

              return isLast ? (
                <Typography key={path} color="#1C1D22" fontSize="14px">
                  {getPathLabel(path)}
                </Typography>
              ) : (
                <StyledLink key={path} component={RouterLink} to={routeTo}>
                  {getPathLabel(path)}
                </StyledLink>
              );
            })}
          </StyledBreadcrumbs>
        </Box>

        {/* Right section with notifications, language, and profile */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Notifications */}
          {/* <IconButton>
            <Badge badgeContent={15} color="warning">
              <NotificationsIcon sx={{ color: "#8B8D97" }} />
            </Badge>
          </IconButton> */}

          {/* Language Selector */}
          <Box sx={{ position: "relative" }}>
            <LanguageButton onClick={() => setToggleLang(!toggleLang)}>
              <img
                style={{
                  color: "#1D1D1F",
                }}
                src={langicon}
                alt="language"
              />
              <Typography
                sx={{ color: "#646464", fontSize: "14px", fontWeight: 400 }}
              >
                {multiLanguage
                  ? multiLanguage[langSelection]?.langName
                  : "Select a lang"}
              </Typography>
              {toggleLang ? (
                <ExpandLess sx={{ color: "grey.700", fontSize: "20px" }} />
              ) : (
                <ExpandMore sx={{ color: "grey.700", fontSize: "20px" }} />
              )}
            </LanguageButton>

            {toggleLang && (
              <LanguageMenu>
                {multiLanguage &&
                  Object.keys(multiLanguage).map((key, index) => (
                    <MenuItem
                      key={key}
                      onClick={() => handleLanguageSelect(index)}
                    >
                      {multiLanguage[key].langName}
                    </MenuItem>
                  ))}
              </LanguageMenu>
            )}
          </Box>

          {/* Profile Section */}
          <Button
            onClick={(e) => {
              setProfileMenu(e.currentTarget);
              navigate("/profile");
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              textTransform: "none",
            }}
          >
            <Avatar
              src={auth?.profile?.profile_image || ""}
              alt={auth?.profile?.firstName || ""}
              sx={{ width: 32, height: 32 }}
            />
            <Typography
              sx={{ color: "#404040", fontSize: "14px", fontWeight: 700 }}
            >
              {auth?.profile?.firstName || "User"}{" "}
              {auth?.profile?.lastName || ""}
            </Typography>
            {/* <ExpandMore sx={{ color: "grey.700", fontSize: "20px" }} /> */}
          </Button>
          {/* <Menu
            anchorEl={profileMenu}
            open={Boolean(profileMenu)}
            onClose={() => setProfileMenu(null)}
          >
            <MenuItem
              onClick={() => {
                navigate("/profile");
                setProfileMenu(null);
              }}
            >
             {t("profile")}
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate("/settings");
                setProfileMenu(null);
              }}
            >
              {t("settings_title")}
            </MenuItem>
            <MenuItem onClick={handleLogout}>{t("logout")}</MenuItem>
          </Menu> */}
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
}
